import { ComponentTemplates } from '../util/component-templates.js';
import { ownsRecordClick } from '../util/record-click.js';
/**
 * <g-card-grid> - Configured card grid
 *
 * Renders a responsive grid of cards from a JS data array. The card interior
 * (title, subtitle, badges with color rules, metric grid, progress bar) is
 * declared via `.cardConfig`; the host doesn't write per-card HTML. Owns
 * pagination, empty/loading states, and card-click events. Filtering and
 * search stay in the host (push filtered data into `.data`).
 *
 * @example
 *   <g-card-grid id="g" page-size="12" empty-text="No companies"></g-card-grid>
 *   <script>
 *     g.cardConfig = {
 *       cols: { default: 1, md: 2, lg: 3 },
 *       title: { field: 'name', classes: 'font-bold' },
 *       subtitle: { field: 'industry' },
 *       badges: [
 *         { field: 'status', colorMap: { Active: 'success', Prospect: 'info' } },
 *         { field: 'leadScore', label: 'Score',
 *           colorRule: [
 *             { when: { gte: 80 }, color: 'success' },
 *             { when: { gte: 60 }, color: 'warning' },
 *             { color: 'error' }
 *           ] }
 *       ],
 *       metrics: [
 *         { field: 'revenue', label: 'Revenue', render: 'currency', currency: 'USD' },
 *         { field: 'employees', label: 'Employees', render: 'number' }
 *       ],
 *       progress: { field: 'capacity', label: 'Capacity', max: 100,
 *         colorRule: [
 *           { when: { gte: 100 }, color: 'error' },
 *           { when: { gte: 80 }, color: 'warning' },
 *           { color: 'success' }
 *         ] },
 *       footer: [
 *         { field: 'location', icon: 'location-dot' },
 *         // A URL column as a real anchor. `render: 'link'` works anywhere
 *         // formatValue does — footer and metrics. Optional `linkLabel`
 *         // replaces the URL text; `linkIcon: false` drops the ↗ glyph.
 *         // Clicking it does not open the card.
 *         { field: 'productUrl', render: 'link', linkLabel: 'View product' }
 *       ]
 *     };
 *     g.data = companies;
 *     g.addEventListener('card-click', e => openDetail(e.detail.row));
 *   </script>
 *
 * Properties:
 *   .cardConfig  card-interior + grid layout config
 *   .data        Array<object> of records
 *
 * Attributes:
 *   page-size    integer (default 24)
 *   empty-text   string (default 'No records found')
 *   loading      boolean attr
 *
 * Events:
 *   card-click   detail: { row, index }
 *   page-change  detail: { page, totalPages }
 */
import { evalPredicate } from '../util/predicate.js';
import { resolveStatus, statusAttr, ensureStatusStyles } from '../util/status.js';
import { safeUrl } from '../util/markdown.js';
import { HUE_TOKENS } from '../util/hues.js';
import { calendarDate } from '../util/dates.js';

const ALLOWED_COLORS = new Set([
  'primary', 'secondary', 'accent', 'success', 'warning', 'error', 'info', 'neutral', 'ghost',
  ...HUE_TOKENS,
]);

const DEFAULT_COLS = { default: 1, md: 2, lg: 3 };

export class GCardGrid extends HTMLElement {
  constructor() {
    super();
    this._data = [];
    this._cardConfig = null;
    this._cardStatus = null;
    this._page = 1;
    this._renderScheduled = false;
    this._templates = new ComponentTemplates(this);
  }

  static get observedAttributes() {
    return ['page-size', 'empty-text', 'loading', 'server-total', 'empty-icon', 'max-height'];
  }

  attributeChangedCallback() { this.scheduleRender(); }

  connectedCallback() {
    this.classList.add('block');
    this._templates.connect();
    ensureStatusStyles();
    if (!this._root) {
      this._root = document.createElement('div');
      this.appendChild(this._root);
    }
    this.scheduleRender();
    this.addEventListener('click', this._handleClick);
  }

  disconnectedCallback() {
    this._templates.disconnect();
    this.removeEventListener('click', this._handleClick);
  }

  // ── Public API ──

  get data() { return this._data; }
  set data(rs) {
    this._data = Array.isArray(rs) ? rs : [];
    // _totalPages() respects server-total, so a server-mode page assignment
    // (always ≤ page-size rows) never snaps the current page back to 1.
    const total = this._totalPages();
    if (this._page > total) this._page = total;
    this.scheduleRender();
  }

  // Card tint — the card-level twin of <g-table>'s .rowStatus. Callback, or the
  // declarative form on cardConfig.status ({ field, colorMap, colorRule }).
  get cardStatus() { return this._cardStatus; }
  set cardStatus(fn) { this._cardStatus = typeof fn === 'function' ? fn : null; this.scheduleRender(); }

  _statusSpec() { return this._cardStatus || (this._cardConfig && this._cardConfig.status) || null; }

  get cardConfig() { return this._cardConfig; }
  set cardConfig(c) {
    this._cardConfig = c && typeof c === 'object' ? c : null;
    this.scheduleRender();
  }

  get pageSize() {
    const n = parseInt(this.getAttribute('page-size'), 10);
    return Number.isFinite(n) && n > 0 ? n : 24;
  }

  // Opt-in server pagination (mirror of <g-table server-total>): `.data` is
  // ONE host-fetched page; no internal slicing; range label counts against
  // the server total; page clicks emit page-change for the host to refetch.
  // Absent attribute → classic client mode, unchanged.
  get serverTotal() {
    if (!this.hasAttribute('server-total')) return null;
    const n = parseInt(this.getAttribute('server-total'), 10);
    return Number.isFinite(n) && n >= 0 ? n : null;
  }

  get emptyIcon() {
    const v = this.getAttribute('empty-icon');
    return v && /^[\w\s-]+$/.test(v) ? v : 'fa-regular fa-inbox';
  }

  // Validated, not escaped: it reaches a style attribute.
  get maxHeight() {
    const v = this.getAttribute('max-height');
    return v && /^\d+(\.\d+)?(px|rem|em|vh|%)$/.test(v) ? v : null;
  }

  get emptyText() {
    return this.getAttribute('empty-text') || 'No records found';
  }

  get loading() {
    return this.hasAttribute('loading');
  }

  setPage(page) {
    const totalPages = this._totalPages();
    const next = Math.max(1, Math.min(page, totalPages));
    if (next === this._page) return;
    this._page = next;
    this.scheduleRender();
    this.dispatchEvent(new CustomEvent('page-change', {
      detail: { page: this._page, totalPages, pageSize: this.pageSize }, bubbles: true, composed: true,
    }));
  }

  // ── Internals ──

  scheduleRender() {
    if (this._renderScheduled) return;
    this._renderScheduled = true;
    requestAnimationFrame(() => {
      this._renderScheduled = false;
      this._render();
    });
  }

  _totalPages() {
    const total = this.serverTotal != null ? this.serverTotal : this._data.length;
    return Math.max(1, Math.ceil(total / this.pageSize));
  }

  _paginated() {
    // Server mode: .data IS the current page.
    if (this.serverTotal != null) return this._data;
    const start = (this._page - 1) * this.pageSize;
    return this._data.slice(start, start + this.pageSize);
  }

  _render() {
    if (!this._root || !this.isConnected) return;
    const templates = this._templates;
    templates.begin();
    const cfg = this._cardConfig || {};
    const cols = cfg.cols || DEFAULT_COLS;
    const gridClasses = buildGridClasses(cols);

    const serverMode = this.serverTotal != null;
    const totalPages = this._totalPages();
    const pageRows = this._paginated();
    const total = serverMode ? this.serverTotal : this._data.length;
    const pageStart = total === 0 ? 0 : (this._page - 1) * this.pageSize;
    const pageEnd = serverMode
      ? Math.min(pageStart + pageRows.length, total)
      : Math.min(pageStart + this.pageSize, total);
    // data-row-index resolves against this._data on click — in server mode
    // .data IS the page, so index from 0.
    const indexBase = serverMode ? 0 : pageStart;

    let body;
    if (this.loading) {
      body = `<div class="${gridClasses}">${repeat(6, skeletonCard)}</div>`;
    } else if (pageRows.length === 0) {
      body = templates.region('empty') || `<div class="text-center py-16 text-base-content/50">
        <i class="${escapeHtml(this.emptyIcon)} text-3xl mb-2 block"></i>
        ${escapeHtml(this.emptyText)}
      </div>`;
    } else {
      const statusSpec = this._statusSpec();
      const cards = pageRows.map((row, i) => {
        const absoluteIndex = indexBase + i;
        const sAttr = statusAttr(resolveStatus(row, absoluteIndex, statusSpec));
        // A tinted card drops `bg-base-100`: both are plain background rules of
        // equal specificity, so leaving it on would make the winner depend on
        // stylesheet order rather than intent.
        const surface = sAttr ? '' : 'bg-base-100 ';
        return `<div class="card ${surface}shadow-sm hover:shadow-md transition-shadow cursor-pointer h-full" data-row-index="${absoluteIndex}"${sAttr}>${renderCard(row, cfg, templates, absoluteIndex)}</div>`;
      }).join('');
      body = `<div class="${gridClasses}">${cards}</div>`;
    }

    const pagination = total > this.pageSize ? this._renderPagination(total, pageStart, pageEnd, totalPages) : '';

    const mh = this.maxHeight;
    const header = templates.region('header');
    templates.commit(header + (mh
      ? `<div class="overflow-auto" style="max-height:${mh}">${body}</div>${pagination}`
      : `${body}${pagination}`));
  }

  _renderPagination(total, pageStart, pageEnd, totalPages) {
    const range = `Showing ${pageStart + 1}–${pageEnd} of ${total}`;
    return `
      <div class="mt-4 flex justify-between items-center">
        <span class="text-sm text-base-content/60">${escapeHtml(range)}</span>
        <div class="join">
          <button class="join-item btn btn-sm" data-page="prev"${this._page <= 1 ? ' disabled' : ''}>«</button>
          <button class="join-item btn btn-sm">${this._page}</button>
          <button class="join-item btn btn-sm" data-page="next"${this._page >= totalPages ? ' disabled' : ''}>»</button>
        </div>
      </div>
    `;
  }

  _handleClick = (e) => {
    const pageBtn = e.target.closest('[data-page]');
    if (pageBtn && this.contains(pageBtn)) {
      const dir = pageBtn.getAttribute('data-page');
      this.setPage(this._page + (dir === 'next' ? 1 : -1));
      return;
    }
    const card = e.target.closest('[data-row-index]');
    if (card && this.contains(card)) {
      // Don't fire on interactive children
      if (ownsRecordClick(e, card)) return;
      const idx = parseInt(card.getAttribute('data-row-index'), 10);
      const row = this._data[idx];
      if (row) {
        this.dispatchEvent(new CustomEvent('card-click', {
          detail: { row, index: idx },
          bubbles: true, composed: true,
        }));
      }
    }
  };
}

// ──────────── Card renderer ────────────

function renderCard(row, cfg, templates, index) {
  const parts = [];
  parts.push('<div class="card-body p-4 gap-2">');

  // Title + subtitle
  if (cfg.title) parts.push(renderCardTitle(row, cfg.title));
  if (cfg.subtitle) parts.push(renderCardSubtitle(row, cfg.subtitle));

  // Badges row
  if (Array.isArray(cfg.badges) && cfg.badges.length) {
    const badgeHtml = cfg.badges.map(b => renderBadge(row, b)).filter(Boolean).join(' ');
    if (badgeHtml) parts.push(`<div class="flex flex-wrap gap-1.5 mt-1">${badgeHtml}</div>`);
  }

  // Metrics grid (2-col label/value)
  if (Array.isArray(cfg.metrics) && cfg.metrics.length) {
    parts.push(renderMetricsGrid(row, cfg.metrics));
  }

  // Progress bar
  if (cfg.progress) parts.push(renderProgress(row, cfg.progress));

  parts.push(templates.region('item-content', row, index));

  // Footer items (icon + value pairs)
  if (Array.isArray(cfg.footer) && cfg.footer.length) {
    parts.push(renderFooter(row, cfg.footer));
  }

  parts.push(templates.region('item-footer', row, index));
  parts.push(templates.region('item-actions', row, index));
  parts.push('</div>');
  return parts.join('\n');
}

function renderCardTitle(row, conf) {
  const v = row[conf.field];
  if (v == null || v === '') return '';
  const cls = `card-title text-base ${conf.classes || ''}`.trim();
  return `<h3 class="${escapeHtml(cls)}">${escapeHtml(String(v))}</h3>`;
}

function renderCardSubtitle(row, conf) {
  const v = row[conf.field];
  if (v == null || v === '') return '';
  return `<p class="text-xs text-base-content/60">${escapeHtml(String(v))}</p>`;
}

function renderBadge(row, b) {
  const v = row[b.field];
  if (v == null || v === '') return '';
  const color = resolveColor(v, b);
  const labelPrefix = b.label ? `${escapeHtml(b.label)}: ` : '';
  return `<span class="badge badge-soft badge-sm badge-${color}">${labelPrefix}${escapeHtml(String(v))}</span>`;
}

function renderMetricsGrid(row, metrics) {
  const cells = metrics.map(m => {
    const v = formatValue(row[m.field], m);
    return `<div>
        <div class="text-[10px] uppercase tracking-wider text-base-content/50">${escapeHtml(m.label || m.field)}</div>
        <div class="text-sm font-medium">${v}</div>
      </div>`;
  }).join('');
  const cols = Math.min(metrics.length, 2);
  return `<div class="grid grid-cols-${cols} gap-3 mt-2">${cells}</div>`;
}

function renderProgress(row, conf) {
  const raw = parseFloat(row[conf.field]);
  if (isNaN(raw)) return '';
  const max = conf.max || 100;
  const pct = Math.max(0, Math.min(100, (raw / max) * 100));
  const color = resolveColor(raw, conf);
  const label = conf.label ? `<div class="flex justify-between text-xs text-base-content/60 mb-0.5"><span>${escapeHtml(conf.label)}</span><span>${pct.toFixed(0)}%</span></div>` : '';
  return `<div class="mt-2">
    ${label}
    <progress class="progress progress-${color} w-full" value="${pct}" max="100"></progress>
  </div>`;
}

function renderFooter(row, footer) {
  const items = footer.map(f => {
    const v = row[f.field];
    if (v == null || v === '') return '';
    const icon = f.icon ? `<i class="fa-regular fa-${escapeHtml(f.icon)} text-base-content/50"></i>` : '';
    const val = formatValue(v, f);
    return `<span class="inline-flex items-center gap-1.5 text-xs text-base-content/70">${icon}<span>${val}</span></span>`;
  }).filter(Boolean).join('');
  if (!items) return '';
  return `<div class="flex flex-wrap gap-3 mt-2 pt-2 border-t border-base-200">${items}</div>`;
}

// ──────────── Render-hint formatting ────────────

// Returns SAFE HTML — every text-bearing branch escapes its own input so
// callers can insert via innerHTML without double-escaping (which would
// strip the icons emitted by the boolean branch). Auto-promotes raw
// boolean values from text-mode columns so untagged Boolean schema fields
// also render as check/cross.
function formatValue(v, conf) {
  if (v == null || v === '') return '—';
  const kind = (conf && conf.render) || 'text';
  if (kind === 'text' && (v === true || v === false)) return renderBoolean(v);
  switch (kind) {
    case 'currency': {
      const n = typeof v === 'number' ? v : parseFloat(v);
      if (isNaN(n)) return escapeHtml(String(v));
      try {
        return escapeHtml(new Intl.NumberFormat('en-US', { style: 'currency', currency: conf.currency || 'USD', maximumFractionDigits: 0 }).format(n));
      } catch { return escapeHtml(String(v)); }
    }
    case 'date': {
      const d = calendarDate(v);
      if (!d) return escapeHtml(String(v));
      return escapeHtml(d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' }));
    }
    case 'datetime': {
      const d = new Date(v);
      if (isNaN(d.getTime())) return escapeHtml(String(v));
      return escapeHtml(d.toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }));
    }
    case 'number': {
      const n = typeof v === 'number' ? v : parseFloat(v);
      if (isNaN(n)) return escapeHtml(String(v));
      return escapeHtml(new Intl.NumberFormat('en-US').format(n));
    }
    case 'percent': {
      const n = typeof v === 'number' ? v : parseFloat(v);
      if (isNaN(n)) return escapeHtml(String(v));
      return escapeHtml((n * 100).toFixed(1) + '%');
    }
    case 'boolean': return renderBoolean(v);
    case 'ref-link': {
      const key = conf.displayField || 'name';
      const label = (x) => (x && typeof x === 'object') ? x[key] : x;
      // Multi-ref (objectIdArray relation) → one soft chip per populated ref.
      if (Array.isArray(v)) {
        const chips = v
          .map(label)
          .filter((x) => x != null && x !== '')
          .map((n) => `<span class="badge badge-soft badge-sm">${escapeHtml(String(n))}</span>`);
        return chips.length ? chips.join(' ') : '—';
      }
      const display = (typeof v === 'object') ? v[key] : v;
      return escapeHtml(String(display ?? '—'));
    }
    // A URL column rendered as a real anchor.
    //
    // The gap this closes: cardConfig could format a URL as text and nothing
    // else, so "make the product link clickable on the card" had no expression
    // in the component at all. A refine hit exactly that on 2026-09-08,
    // concluded the component could not do it — correctly — and then deleted
    // <g-card-grid> and rebuilt the whole grid by hand to get one anchor,
    // losing pagination, the loading skeleton and the empty state with it.
    //
    // Clicking the anchor does NOT open the card: _handleClick already returns
    // early for `a` (alongside button/input/select/textarea/label), so the
    // component was ready for this before there was a way to ask for it.
    //
    // `safeUrl` is the shared allow-list from util/markdown — relative, mailto
    // and http(s) only. A rejected value still RENDERS, as plain text: data is
    // data, and silently dropping a row's field because its URL is odd is
    // worse than showing it un-clickable.
    case 'link': {
      const raw = String(v).trim();
      const href = safeUrl(raw);
      const label = escapeHtml(conf.linkLabel || raw);
      if (!href) return escapeHtml(raw);
      const external = /^(https?:|mailto:)/i.test(href);
      const target = external ? ' target="_blank" rel="noopener noreferrer"' : '';
      const icon = conf.linkIcon === false ? ''
        : `<i class="fa-regular fa-arrow-up-right-from-square text-[0.85em]" aria-hidden="true"></i>`;
      return `<a href="${escapeHtml(href)}"${target} class="link link-primary inline-flex items-center gap-1 max-w-full"><span class="truncate">${label}</span>${icon}</a>`;
    }
    case 'text':
    default: return escapeHtml(String(v));
  }
}

// Boolean cell: green check for truthy, red x for falsy. Mirrors g-table so the
// two widgets stay consistent on the same page — including the icon set, which
// is Font Awesome like everything else here.
//
// This comment used to cite g-table's claim that FA cannot be injected via
// innerHTML. The claim was wrong, and citing it is how one file's mistake
// became two.
const SVG_CHECK = `<i class="fa-regular fa-circle-check" aria-hidden="true"></i>`;
const SVG_XMARK = `<i class="fa-regular fa-circle-xmark" aria-hidden="true"></i>`;

function renderBoolean(v) {
  if (v == null || v === '') return '—';
  const truthy = v === true || v === 1 || v === '1' || v === 'true' || v === 'yes';
  const falsy = v === false || v === 0 || v === '0' || v === 'false' || v === 'no';
  if (!truthy && !falsy) return escapeHtml(String(v));
  return truthy
    ? `<span class="text-success inline-flex" title="Yes" aria-label="Yes">${SVG_CHECK}</span>`
    : `<span class="text-error inline-flex" title="No" aria-label="No">${SVG_XMARK}</span>`;
}

// ──────────── Color resolvers ────────────

function resolveColor(value, conf) {
  // colorMap: { 'Active': 'success', 'Pending': 'warning' } — strict equality
  if (conf.colorMap && typeof conf.colorMap === 'object') {
    const c = conf.colorMap[value];
    if (c && ALLOWED_COLORS.has(c)) return c;
  }
  // colorRule: array of { when?: predicate, color }, evaluated in order, first match wins
  if (Array.isArray(conf.colorRule)) {
    for (const rule of conf.colorRule) {
      if (!rule.when) {
        // No condition = default catch-all
        if (ALLOWED_COLORS.has(rule.color)) return rule.color;
      } else {
        if (evalPredicate(value, rule.when) && ALLOWED_COLORS.has(rule.color)) return rule.color;
      }
    }
  }
  return 'neutral';
}

// ──────────── Layout helpers ────────────

function buildGridClasses(cols) {
  // cols = { default, sm?, md?, lg?, xl? }
  const out = ['grid', 'gap-4'];
  if (cols.default) out.push(`grid-cols-${cols.default}`);
  if (cols.sm) out.push(`sm:grid-cols-${cols.sm}`);
  if (cols.md) out.push(`md:grid-cols-${cols.md}`);
  if (cols.lg) out.push(`lg:grid-cols-${cols.lg}`);
  if (cols.xl) out.push(`xl:grid-cols-${cols.xl}`);
  return out.join(' ');
}

function repeat(n, fn) {
  let out = '';
  for (let i = 0; i < n; i++) out += fn();
  return out;
}

function skeletonCard() {
  return `<div class="card bg-base-100 shadow-sm h-full">
    <div class="card-body p-4 gap-2">
      <div class="skeleton h-5 w-3/4"></div>
      <div class="skeleton h-3 w-1/2"></div>
      <div class="skeleton h-4 w-full mt-2"></div>
    </div>
  </div>`;
}

function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
}

if (!customElements.get('g-card-grid')) {
  customElements.define('g-card-grid', GCardGrid);
}
